import React, { FC, useState } from 'react'
import { Accordion, Heading, Icon, Text, useDesignLanguagePerClass, useToast } from '@afterpaytouch/core'
import { Trans } from 'react-i18next'
import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { InlinePaymentDayList } from '../PaymentDayList/InlinePaymentDayList'
import {
  useAppDispatch,
  useCheckoutToken,
  useExpandedComponentControl,
  useIsHidePaymentDay,
  useProfilingId,
  useSelectedPreferredDay,
  useShowPreferredPaymentDay,
  useUpdatePreferredPaydayMutation,
} from '../../state'
import { setPreferredPaymentDayAndStatus } from '../../state/preferredPaymentDay/reducer'
import { ExpandableComponent, PreferredPaymentDayStatus, Weekday } from '../../model'
import { TrackingEvent } from '../../model/monitoring'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { getLocaleKeyFromDayNumber, getUppercaseDayNameFromDayNumber } from '../../utils/calendar'
import styles from './styles.module.css'

export const PreferredPaymentDayInline: FC = () => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { textSize } = useCheckoutSizes()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const setToast = useToast()
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const showPreferredPaymentDay = useShowPreferredPaymentDay()
  const hidePaymentDay = useIsHidePaymentDay()
  const defaultSelectedDay = useSelectedPreferredDay()
  const [selectedDay, setSelectedDay] = useState(defaultSelectedDay)
  const [isLoading, setIsLoading] = useState(false)
  const [hasManuallySet, setHasManuallySet] = useState(false)

  const { setExpandedState, toggleExpandedState, expandableComponentState } = useExpandedComponentControl(ExpandableComponent.PREFERRED_PAYMENT_DAY_INLINE)
  const isExpanded = expandableComponentState === 'Expanded'

  const [updatePreferredPayday] = useUpdatePreferredPaydayMutation()
  const profilingSessionId = useProfilingId()
  const token = useCheckoutToken()

  if (hidePaymentDay || (!showPreferredPaymentDay && !hasManuallySet)) {
    return null
  }

  const handleAccordionChange = (): void => {
    const newState = isExpanded ? 'Collapsed' : 'Expanded'
    logEvent(TrackingEvent.PRESSED_SUMMARY_SECTION, {
      section: 'Preferred Payment Day',
      state: newState,
    })
    toggleExpandedState()
  }

  const handleDaySelect = (day: Weekday): void => {
    setIsLoading(true)
    logEvent(TrackingEvent.PRESSED_ALIGN_PAYMENT_SAVE_BUTTON)

    updatePreferredPayday({
      token,
      profilingSessionId,
      preferredPaymentDay: getUppercaseDayNameFromDayNumber(day),
    })
      .unwrap()
      .then(() => {
        setSelectedDay(day)
        setHasManuallySet(true)
        dispatch(
          setPreferredPaymentDayAndStatus({
            paymentDay: day,
            status: PreferredPaymentDayStatus.ManuallySet,
          })
        )

        logEvent(TrackingEvent.ALIGN_PAYMENT_SAVE_SUCCESS)
        setToast({
          kind: 'Success',
          testNameSpace: 'preferredDay',
          message: () => (
            <Text>
              <Trans
                i18nKey='preferredPaymentDay:saveSuccessMessage'
                values={{
                  selectedPaymentDay: t(getLocaleKeyFromDayNumber(day)),
                }}
                components={{
                  b: <strong />,
                }}
              />
            </Text>
          ),
        })
      })
      .catch(() => {
        logEvent(TrackingEvent.ALIGN_PAYMENT_SAVE_FAILURE)
        setToast({
          kind: 'Failure',
          testNameSpace: 'preferredDay',
          message: () => (
            <Text>
              <Trans i18nKey='preferredPaymentDay:saveFailureMessage' />
            </Text>
          ),
        })
      })
      .finally(() => {
        setIsLoading(false)
        setExpandedState('Collapsed')
      })
  }

  const renderCollapsedContent = (): JSX.Element => (
    <div className={designLanguageStyles.collapsedContainer}>
      <Icon name='RecurringAutomatic' size='Medium' />
      <div className={designLanguageStyles.textContainer}>
        <Heading size='M' renderAs='span'>
          {t('preferredPaymentDay:inlineTitle')}
        </Heading>
        <Text size={textSize} renderAs='div' color='Subtle'>
          {t(getLocaleKeyFromDayNumber(selectedDay))}
        </Text>
      </div>
    </div>
  )

  return (
    <div className={designLanguageStyles.wrapper}>
      <Accordion
        id={ExpandableComponent.PREFERRED_PAYMENT_DAY_INLINE}
        expanded={isExpanded}
        onChange={handleAccordionChange}
        testNameSpace='preferred-payment-day-inline'
        disabled={isLoading}
      >
        <Accordion.Button>{renderCollapsedContent()}</Accordion.Button>
        <Accordion.Details>
          <div className={designLanguageStyles.messageContainer}>
            <Text size={textSize} color='Subtle'>
              {t('preferredPaymentDay:inlineDescription')}
            </Text>
          </div>
          <InlinePaymentDayList onChange={handleDaySelect} selectedDay={selectedDay} />
        </Accordion.Details>
      </Accordion>
      <TrackOnMount eventName={TrackingEvent.VIEWED_ALIGN_PAYMENT_MESSAGE} />
    </div>
  )
}
